<template>
    <div class="lms__superadmin_users_profile_edit_learninghistory">
        <div class="lms__superadmin_users_profile_edit_learninghistory_header">
            <div class="container">
                <div class="columns">
                    <div class="column">
                        <component-title :size="'extra-large'" :raw="true">{{ userName }} learning history</component-title>
                        <component-text>
                            <template v-if="loggedInUserTokenWorkspaceId">
                                You are viewing history from this workspace.
                                To view the learning history over your workspaces, leave this workspace and view the history from there.
                            </template>
                            <template v-else>
                                You are viewing this user's history from all workspaces.
                            </template>
                        </component-text>
                    </div>
                    <div class="column is-narrow">
                        <component-button
                            :name="'lms-superadmin-users-profile-edit-learning-history-backtousers'"
                            :to="{ name: 'lms-superadmin-user-list' }"
                            :theme="'default'"
                        >Back to users page</component-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="lms__superadmin_users_profile_edit_learninghistory_tabs">
            <div class="container">
                <div class="columns is-variable is-0">
                    <div class="column">
                        <div class="columns">
                            <div class="column is-narrow">
                                <component-button
                                    :name="'lms-superadmin-users-profile-edit-learning-history-courses'"
                                    :theme="'nav'"
                                    :size="'medium'"
                                    :is-active="routeQueryView === 'courses'"
                                    :secondary-theme="routeQueryView === 'courses' ? 'primary' : ''"
                                    @click="switchView('courses')"
                                >Courses
                                </component-button>
                            </div>
                            <div class="column is-narrow">
                                <component-button
                                    :name="'lms-superadmin-users-profile-edit-learning-history-events'"
                                    :theme="'nav'"
                                    :size="'medium'"
                                    :is-active="routeQueryView === 'events'"
                                    :secondary-theme="routeQueryView === 'events' ? 'primary' : ''"
                                    @click="switchView('events')"
                                >Events
                                </component-button>
                            </div>
                            <div class="column is-narrow">
                                <component-button
                                    :name="'lms-superadmin-users-profile-edit-learning-history-external'"
                                    :theme="'nav'"
                                    :size="'medium'"
                                    :is-active="routeQueryView === 'external'"
                                    :secondary-theme="routeQueryView === 'external' ? 'primary' : ''"
                                    @click="switchView('external')"
                                >External records
                                </component-button>
                            </div>
                        </div>
                    </div>
                    <div class="column"></div>
                    <div class="column is-narrow">
                        <div class="columns">
                            <div class="column is-narrow">
                                <component-button
                                    :name="'lms-superadmin-users-profile-edit-learning-history-tabs-export'"
                                    :icon="true"
                                    :icon-name="'download'"
                                    :icon-align="'right'"
                                    :theme="'default'"
                                    @click="performExportHistory"
                                >Export history
                                </component-button>
                            </div>
                            <template v-if="routeQueryView === 'external'">
                                <div class="column is-narrow">
                                    <component-button
                                        :name="'lms-superadmin-users-profile-edit-learning-history-tabs-addexternal'"
                                        :disabled="!user.hasSelectedProfile()"
                                        :disabled-hint-active="!user.hasSelectedProfile()"
                                        :disabled-hint-text="'Access a workspace to add an external record.'"
                                        @click="openNotifyExternalTraining"
                                    >Add external training
                                    </component-button>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="lms__superadmin_users_profile_edit_learninghistory_main">
            <template v-if="routeQueryView === 'courses'">
                <lms-superadmin-users-profile-edit-learning-history-courses></lms-superadmin-users-profile-edit-learning-history-courses>
            </template>
            <template v-if="routeQueryView === 'events'">
                <lms-superadmin-users-profile-edit-learning-history-events></lms-superadmin-users-profile-edit-learning-history-events>
            </template>
            <template v-if="routeQueryView === 'external'">
                <lms-superadmin-users-profile-edit-learning-history-external></lms-superadmin-users-profile-edit-learning-history-external>
            </template>
        </div>
        <pop-notify
            ref="notifyexternaltraining"
            :title-text="'External training'"
            :content-text="`Please complete the details of the completed course below and optionally upload any evidence such as a certificate. If the course has an equivalent in your catalogue you can choose to link it using the 'Equals to' field.`"
            :size="'medium'"
            :submit-button-text="'Submit'"
            :submit-button-animate-loading-name="'create-external-history'"
            :cancel-button-text="'Cancel'"
            :prevent-close="true"
            :slot-form-active="true"
            @emit-submit="performCreateExternalLearningHistory"
        >
            <form-text
                :label="'Course name'"
                :name="'lms-superadmin-users-profile-edit-learning-history-course-name'"
                :data-vv-as="'course name'"
                :required="true"
                :v-validate="'required'"
                v-model="externalHistoryCourseName"
            ></form-text>
            <div class="columns is-variable">
                <div class="column">
                    <form-date
                        :label="'Completion date'"
                        :name="'lms-superadmin-users-profile-edit-learning-history-completion-date'"
                        :data-vv-as="'date'"
                        :required="true"
                        :v-validate="{ required: true, date_format: 'yyyy-MM-dd', before: tomorrowsDate }"
                        v-model="externalHistoryCompletionDateDate"
                    >
                    </form-date>
                </div>
                <div class="column is-narrow">
                    <form-time
                        :label="'Time'"
                        :name="'lms-superadmin-users-profile-edit-learning-history-completion-time'"
                        :data-vv-as="'time'"
                        :required="true"
                        :v-validate="{ required: true, date_format: 'HH:mm' }"
                        :placeholder="'00:00'"
                        v-model="externalHistoryCompletionDateTime"
                    />
                </div>
            </div>
            <form-image
                :label="'Certificate'"
                :name="'lms-superadmin-users-profile-edit-learning-history-certificate'"
                :data-vv-as="'certificate'"
                :formats="'.jpg, .png, .jpeg, .pdf'"
                :value="externalHistoryCertificate"
                @emit-select="externalHistoryCertificateUploadFile"
                @emit-remove="externalHistoryCertificateRemoveFile"
            />
            <form-text
                :label="'Provider'"
                :name="'lms-superadmin-users-profile-edit-learning-history-provider'"
                :data-vv-as="'provider'"
                v-model="externalHistoryCompletionProvider"
            ></form-text>
            <form-text
                :label="'Accreditation'"
                :name="'lms-superadmin-users-profile-edit-learning-history-accreditation'"
                :data-vv-as="'accreditation'"
                v-model="externalHistoryCompletionAccreditation"
            ></form-text>
            <form-select
                :label="'Equals to'"
                :name="'lms-superadmin-users-profile-edit-learning-history-course'"
                :data-vv-as="'courses'"
                :get-pagination-getter="'getEbsCatalogItemsPagination'"
                :get-items-getter="'getEbsCatalogItemsMappedIdNameStatus'"
                :get-items-getter-mapped="'getEbsCatalogItemsMappedLabelValue'"
                :get-items-dispatch="'ebsCatalogItemsGet'"
                :get-items-dispatch-data="{ workspaceId: routeParamsWorkspaceId }"
                :search-queryable="true"
                :table-labels="[
                    {
                        key: 'name',
                        title: 'Name',
                        type: 'string',
                        sortable: true,
                        queryable: true
                    }
                ]"
                :item-name="['course', 'courses']"
                :value="externalHistoryCompletionMappingCatalogItemId"
                @input="lmsSuperadminUserLearningHistorySetMappingCatalogItemId"
                @input-table="lmsSuperadminUserLearningHistorySetMappingCatalogItemId"
            ></form-select>
        </pop-notify>
    </div>
</template>

<script>

import LmsSuperadminUsersProfileEditLearningHistoryTable from '@/components/lms/superadmin/users/profile/edit/LmsSuperadminUsersProfileEditLearningHistoryTable'
import LmsSuperadminUsersProfileEditLearningHistoryExternalTable from '@/components/lms/superadmin/users/profile/edit/LmsSuperadminUsersProfileEditLearningHistoryExternalTable'
import PopNotify from '@/components/default/shared/PopNotify'
import FormText from '@/components/default/forms/FormText'
import FormDate from '@/components/default/forms/FormDate'
import FormTime from '@/components/default/forms/FormTime'
import { toISODateUTC } from '@/functions'
import FormImage from '@/components/default/forms/FormImage'
import FormSelect from '@/components/default/forms/FormSelect'
import { User } from '@/User'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import ComponentButton from '@/components/default/shared/ComponentButton'
import LmsSuperadminUsersProfileEditLearningHistoryCourses from '@/components/lms/superadmin/users/profile/edit/LmsSuperadminUsersProfileEditLearningHistoryCourses'
import LmsSuperadminUsersProfileEditLearningHistoryEvents from '@/components/lms/superadmin/users/profile/edit/LmsSuperadminUsersProfileEditLearningHistoryEvents'
import LmsSuperadminUsersProfileEditLearningHistoryExternal from '@/components/lms/superadmin/users/profile/edit/LmsSuperadminUsersProfileEditLearningHistoryExternal'
import api from '@/api'
import { TokenHelper } from '@/TokenHelper'
import ComponentText from '@/components/default/shared/ComponentText'

export default {
  name: 'lms-superadmin-users-profile-edit-learning-history',
  data () {
    return {
      LmsSuperadminUsersProfileEditLearningHistoryTable,
      LmsSuperadminUsersProfileEditLearningHistoryExternalTable,
      user: new User()
    }
  },
  components: {
    ComponentText,
    LmsSuperadminUsersProfileEditLearningHistoryExternal,
    LmsSuperadminUsersProfileEditLearningHistoryEvents,
    LmsSuperadminUsersProfileEditLearningHistoryCourses,
    ComponentButton,
    ComponentTitle,
    FormSelect,
    FormImage,
    FormTime,
    FormDate,
    FormText,
    PopNotify
  },
  computed: {
    loggedInUserTokenWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    routeQueryView () {
      return this.$route.query.view
    },
    routeParamsWorkspaceId () {
      return this.$route.params.workspaceId
    },
    userId () {
      return this.$route.params.userId
    },
    tomorrowsDate () {
      return toISODateUTC(new Date().getTime() + 1000 * 60 * 60 * 24)
    },
    userName () {
      return this.$store.getters.getEbsUserName
    },
    profileWorkspaceId () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileWorkspaceId
    },
    profileUserId () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileUserId
    },
    profileCustomerId () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileCustomerId
    },
    externalHistoryCourseName: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLearningHistoryCourseName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserLearningHistorySetCourseName', value)
      }
    },
    externalHistoryCompletionDateDate: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLearningHistoryCompletionDateDate
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserLearningHistorySetCompletionDateDate', value)
      }
    },
    externalHistoryCompletionDateTime: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLearningHistoryCompletionDateTime
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserLearningHistorySetCompletionDateTime', value)
      }
    },
    externalHistoryCertificate: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLearningHistoryCertificate
      }
    },
    externalHistoryCompletionProvider: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLearningHistoryProvider
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserLearningHistorySetProvider', value)
      }
    },
    externalHistoryCompletionAccreditation: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLearningHistoryAccreditation
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserLearningHistorySetAccreditation', value)
      }
    },
    externalHistoryCompletionMappingCatalogItemId: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLearningHistoryMappingCatalogItemId
      }
    },
    exportHistoryUrl () {
      const learnerId = this.$route.params.userId
      const token = TokenHelper.getToken('secondStageToken')

      return `${api.learningactivities}/learner-history-xls/${learnerId}?access_token=${token}`
    }
  },
  methods: {
    switchView (value) {
      this.$router.push({ query: { view: value } })
    },
    initializeRouteQuery () {
      this.switchView('courses')
    },
    openNotifyExternalTraining () {
      this.$store.dispatch('lmsSuperadminUserLearningHistoryClear')
      this.$refs.notifyexternaltraining.openNotify()
    },
    performExportHistory () {
      const learnerId = this.$route.params.userId
      const learnerName = this.userName
      this.$store.dispatch('exportUserHistory', { learnerId, learnerName })
    },
    performCreateExternalLearningHistory () {
      this.$store.dispatch('lmsSuperadminUserLearningHistoryCreate', { user: this.userId }).then(() => {
        this.$refs.notifyexternaltraining.closeNotify()
        this.$eventBus.$emit('get-learning-history')
      }).finally(() => {
        this.$eventBus.$emit('stop-create-external-history')
      })
    },
    externalHistoryCertificateUploadFile (data) {
      this.$store.dispatch('lmsSuperadminUserLearningHistorySetCertificate', data)
    },
    externalHistoryCertificateRemoveFile () {
      this.$store.dispatch('lmsSuperadminUserLearningHistorySetCertificate')
    },
    lmsSuperadminUserLearningHistorySetMappingCatalogItemId (data) {
      this.$store.dispatch('lmsSuperadminUserLearningHistorySetMappingCatalogItemId', data.value)
    },
    performReloadInternalRecords () {
      this.$refs.listinternaltraining.getItems()
    }
  },
  created () {
    this.$eventBus.$on('perform-get-enrolment-modules', this.performReloadInternalRecords)
    this.initializeRouteQuery()
  },
  beforeDestroy () {
    this.$eventBus.$off('perform-get-enrolment-modules')
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/users/_lmssuperadminusersprofileeditlearninghistory.scss";
</style>
